<template>
  <div class="container order-container">
    <base-nav title="Заказы">
      <base-button
        v-if="orders.length > 0"
        @click="exportToExcel"
      >
        Экспорт в Excel
      </base-button>
      <base-primary-button
        icon="addSquare"
        @click="goToOrderCreate"
      >
        Создать
      </base-primary-button>
    </base-nav>

    <!-- Фильтры -->
    <FilterBlock
      ref="filterBlock"
      :search-fields="searchFields"
      :initial-filters="appliedFilters"
      target-type="Order"
      @filter-change="handleFilterChange"
    />

    <!-- Счетчик результатов -->
    <MetaResultCount
      v-if="!dataIsLoading && orders && orders.length > 0"
      :pagination-meta="pagination"
    />

    <!-- Переключение вида -->
    <TableToolbar
      v-if="!dataIsLoading && orders.length > 0"
      :show-settings="currentView === 'table'"
      :actions="currentView === 'table' ? dropdownItems : []"
      @open-settings="showSettingsModal = true"
    >
      <base-view-toggle v-model="currentView" />
    </TableToolbar>

    <base-loader v-if="dataIsLoading" />

    <!-- Табличный вид -->
    <div
      v-else-if="
        !dataIsLoading &&
        orders &&
        orders.length > 0 &&
        currentView === 'table'
      "
      class="order-center"
    >
      <TableComponent
        :data="orders"
        :columns="tableColumns"
        :ready="!dataIsLoading && tableColumns.length > 0"
        selectable
        @selection-change="onSelectionChange"
        @row-middle-click="
          openRouteInNewTab({
            name: 'OrderDetails',
            params: { id: $event.id },
          })
        "
        @row-click="goToOrderDetails"
      >
        <template #cell-status="{ item }">
          <BaseStatusSelect
            compact
            :value="item.status"
            :label="orderStore.formatOrderStatus(item.status)"
            :color="orderStore.orderStatusColor(item.status)"
            :options="orderStore.orderStatusSelectOptions"
            @change="handleOrderStatusChange(item, $event)"
          />
        </template>

        <template #cell-payment_status="{ item }">
          <BaseStatusSelect
            compact
            :value="item.payment_status"
            :label="
              orderStore.formatPaymentStatus(item.payment_status)
            "
            :color="
              orderStore.paymentStatusColor(item.payment_status)
            "
            :options="orderStore.paymentStatusSelectOptions"
            @change="handlePaymentStatusChange(item, $event)"
          />
        </template>

        <template #cell-order_type="{ item }">
          <BaseStatusSelect
            compact
            :value="
              item.order_type ? String(item.order_type.id) : null
            "
            :label="item.order_type ? item.order_type.name : '—'"
            :color="
              item.order_type ? item.order_type.color : '#9ca3af'
            "
            :options="orderStore.orderTypeSelectOptions"
            @change="handleOrderTypeChange(item, $event)"
          />
        </template>
      </TableComponent>
    </div>

    <!-- Карточный вид -->
    <div
      v-else-if="
        !dataIsLoading &&
        orders &&
        orders.length > 0 &&
        currentView === 'cards'
      "
      class="orders-block"
    >
      <OrderCardList
        :orders="orders"
        @card-click="goToOrderDetails"
      />
    </div>

    <!-- Пагинация для табличного вида -->
    <pagination-component
      v-if="
        pagination.pages > 1 &&
        !dataIsLoading &&
        tableColumns.length > 0
      "
      :page="pagination.page"
      :pages="pagination.pages"
      :series="pagination.series"
      :route-query="currentRouteQuery"
    />

    <div class="no-data" v-if="!dataIsLoading && orders.length == 0">
      Список заказов пуст
    </div>

    <!-- Модальное окно настроек -->
    <Teleport to="body">
      <TableSettingsModal
        v-if="showSettingsModal"
        resource-model="Order"
        record-type="Order"
        table-name="orders"
        @close="showSettingsModal = false"
        @config-saved="handleConfigSaved"
      />
    </Teleport>

    <!-- Модальное окно дублирования -->
    <DuplicateModal
      v-if="showDuplicateModal"
      :options="duplicateOptions"
      :initial-selected="initialDuplicateOptions"
      @confirm="handleDuplicateConfirm"
      @close="closeDuplicateModal"
    />
  </div>
</template>

<script>
import FilterBlock from "@/components/Filter/FilterBlock.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";
import MetaResultCount from "@/components/Meta/MetaResultCount.vue";
import TableComponent from "@/components/TableComponent.vue";
import DuplicateModal from "@/components/DuplicateModal.vue";
import TableSettingsModal from "@/components/TableSettingsModal.vue";
import TableToolbar from "@/components/Table/TableToolbar.vue";
import OrderCardList from "@/components/Order/OrderCardList.vue";

import ordersApi from "@/services/api/ordersApi";

import rawSearchFields from "@/config/filters/order/orders.json";

import { useOrderStore } from "@/stores/orderStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { useTableConfigStore } from "@/stores/tableConfigStore";

import { getCompanyId } from "@/utils/userUtils";
import { exportToExcel } from "@/utils/fileUtils";
import { openRouteInNewTab } from "@/utils/routeUtils";
import {
  filtersToApiParams,
  getInitialFilters,
  getCurrentPageFromUrl,
  updateUrlWithFilters,
  clearUrlFilters,
} from "@/utils/filterUtils";

export default {
  name: "OrdersPage",
  components: {
    FilterBlock,
    PaginationComponent,
    MetaResultCount,
    TableComponent,
    DuplicateModal,
    TableSettingsModal,
    TableToolbar,
    OrderCardList,
  },
  data() {
    return {
      companyId: getCompanyId(),
      dataIsLoading: true,
      showSettingsModal: false,

      currentView: "table",

      orders: [],
      tableColumns: [],
      selectedOrders: [],

      appliedFilters: {
        searchValues: {},
        showInactive: false,
      },

      showDuplicateModal: false,
      dropdownItems: [
        {
          id: "duplicate",
          label: "Дублировать",
          action: this.openDuplicateModal,
        },
        {
          id: "enable",
          label: "Показать",
          action: this.enableSelected,
        },
        {
          id: "disable",
          label: "Скрыть",
          isDanger: true,
          action: this.disableSelected,
        },
      ],

      duplicateOptions: [
        {
          id: "include_order_points",
          label: "Включая точки маршрута",
          description: "Скопировать все точки маршрута",
        },
        {
          id: "include_trips",
          label: "Включая рейсы",
          description: "Скопировать связанные рейсы",
        },
      ],
      initialDuplicateOptions: {
        include_order_points: true,
        include_trips: true,
      },

      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
        from: 0,
        to: 0,
      },
    };
  },
  watch: {
    "$route.query": {
      immediate: false,
      handler() {
        this.updateFiltersFromUrl();
        this.getOrdersRequest();
      },
    },

    currentView(newView, oldView) {
      if (newView !== oldView) {
        this.selectedOrders = [];
      }
    },
  },
  computed: {
    orderStore() {
      return useOrderStore();
    },

    notificationStore() {
      return useNotificationStore();
    },

    tableConfigStore() {
      return useTableConfigStore();
    },

    currentRouteQuery() {
      return this.$route.query;
    },

    searchFields() {
      return rawSearchFields.map((field) => {
        if (!field.optionsFrom) return field;

        const [store, prop] = field.optionsFrom.split(".");
        const { optionsFrom, ...rest } = field;

        return {
          ...rest,
          options: this[store]?.[prop] ?? [],
        };
      });
    },
  },
  methods: {
    async handleOrderStatusChange(order, status) {
      try {
        await this.orderStore.updateStatus(order.id, status);
        order.status = status;
        this.notificationStore.showSuccess("Статус заказа обновлён");
      } catch {
        // ошибка перехватывается глобально в apiService
      }
    },

    async handlePaymentStatusChange(order, paymentStatus) {
      try {
        await this.orderStore.updatePaymentStatus(
          order.id,
          paymentStatus
        );
        order.payment_status = paymentStatus;
        this.notificationStore.showSuccess("Статус оплаты обновлён");
      } catch {
        // ошибка перехватывается глобально в apiService
      }
    },

    async handleOrderTypeChange(order, orderTypeId) {
      try {
        await this.orderStore.updateOrderType(order.id, orderTypeId);
        const type = this.orderStore.orderTypes.find(
          (t) => String(t.id) === orderTypeId
        );
        order.order_type = type || null;
        this.notificationStore.showSuccess("Тип заказа обновлён");
      } catch {
        // ошибка перехватывается глобально в apiService
      }
    },

    async loadTableConfig() {
      try {
        const columns =
          await this.tableConfigStore.initTableConfiguration(
            "Order",
            "orders"
          );

        this.tableColumns = columns;
      } catch (error) {
        console.error(
          "Не удалось загрузить конфигурацию таблицы заказов",
          error
        );

        this.tableColumns = [];
      }
    },

    handleConfigSaved() {
      this.loadTableConfig();
    },

    async getOrdersRequest() {
      const page = getCurrentPageFromUrl(this.$route);

      try {
        this.dataIsLoading = true;

        const params = {
          page: page,
          ...filtersToApiParams(this.appliedFilters),
        };

        const response = await ordersApi.index(
          this.companyId,
          params
        );

        this.orders =
          this.tableConfigStore.enrichRecordsWithCustomFields(
            response.data.result,
            this.tableColumns
          );

        const meta = response.data.pagination_meta;
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
          from: meta.from,
          to: meta.to,
        };

        this.selectedOrders = [];
      } catch (error) {
        console.error("Ошибка при загрузке списка заказов", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    updateFiltersFromUrl() {
      this.appliedFilters = getInitialFilters(
        this.$route,
        this.searchFields,
        {
          searchValues: {},
          showInactive: false,
        }
      );
    },

    handleFilterChange(filters) {
      this.appliedFilters = { ...filters };

      const hasValues = Object.values(filters.searchValues).some(
        (v) => v !== null && v !== undefined && v !== ""
      );

      if (hasValues || filters.showInactive) {
        updateUrlWithFilters(this.$router, filters, 1);
      } else {
        clearUrlFilters(this.$router);
      }

      this.selectedOrders = [];
    },

    goToOrderDetails(order) {
      const orderId = order.id || order;
      this.$router.push({
        name: "OrderDetails",
        params: { id: orderId },
      });
    },

    goToOrderCreate() {
      this.$router.push({ name: "OrderCreate" });
    },

    async exportToExcel() {
      try {
        const search_params = filtersToApiParams(this.appliedFilters);

        const response = await ordersApi.export(
          this.companyId,
          search_params
        );

        exportToExcel(response, "orders.xlsx");
      } catch (error) {
        console.error("Ошибка при экспорте в Excel:", error);
      }
    },

    onSelectionChange(selected) {
      this.selectedOrders = selected;
    },

    async enableSelected() {
      if (this.selectedOrders.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы один заказ"
        );
        return;
      }

      try {
        const enablePromises = this.selectedOrders.map((order) =>
          ordersApi.enable(this.companyId, order.id)
        );

        await Promise.all(enablePromises);

        this.notificationStore.showSuccess(
          `Успешно включено ${this.selectedOrders.length} заказов`
        );

        await this.getOrdersRequest();
      } catch (error) {
        console.error("Ошибка при включении заказов:", error);
      }
    },

    async disableSelected() {
      if (this.selectedOrders.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы один заказ"
        );
        return;
      }

      try {
        const disablePromises = this.selectedOrders.map((order) =>
          ordersApi.disable(this.companyId, order.id)
        );

        await Promise.all(disablePromises);

        this.notificationStore.showSuccess(
          `Успешно отключено ${this.selectedOrders.length} заказов`
        );

        await this.getOrdersRequest();
      } catch (error) {
        console.error("Ошибка при отключении заказов:", error);
      }
    },

    openDuplicateModal() {
      if (this.selectedOrders.length === 0) {
        this.notificationStore.showWarning(
          "Выберите хотя бы один заказ"
        );
        return;
      }

      this.showDuplicateModal = true;
    },

    closeDuplicateModal() {
      this.showDuplicateModal = false;
    },

    async handleDuplicateConfirm(options) {
      if (this.selectedOrders.length === 0) return;

      try {
        this.dataIsLoading = true;

        const duplicatePromises = this.selectedOrders.map((order) =>
          ordersApi.duplicate(this.companyId, order.id, options)
        );

        await Promise.all(duplicatePromises);

        const count = this.selectedOrders.length;
        this.notificationStore.showSuccess(
          `Успешно продублировано ${count} заказов`
        );

        await this.getOrdersRequest();

        this.closeDuplicateModal();
      } catch (error) {
        console.error("Ошибка при дублировании заказов:", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    openRouteInNewTab,
  },
  async created() {
    this.updateFiltersFromUrl();
    await Promise.all([
      this.loadTableConfig(),
      this.orderStore.fetchOrderTypes(),
    ]);
  },

  async mounted() {
    const storedFilters =
      await this.$refs.filterBlock?.loadStoredFilter();

    if (storedFilters) {
      this.appliedFilters = storedFilters;
      updateUrlWithFilters(this.$router, storedFilters, 1);
    }

    await this.getOrdersRequest();
  },
};
</script>
